import { Divider, InputAdornment, TextField } from "@mui/material";
import { MapPinIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useIntl } from "react-intl";

import { AirplaneTiltIcon } from "@/components/Icons/rtlReadyIcons";
import { ResetButton } from "@/components/ResetButton/ResetButton";
import {
  SearchResultButton,
  SearchResultButtonSkeleton,
  SearchResultList,
} from "@/components/SearchResultList/SearchResultList";
import { useAirportSearch } from "@/features/flights/hooks/useAirportSearch";
import { AirportType } from "@/features/flights/types/types";
import { TopLocationList } from "@/features/search/components/TopLocationList/TopLocationList";
import { useInputAutoFocus } from "@/hooks/useInputAutoFocus";
import { usePreviousAirportSearches } from "@/hooks/usePreviousSearches";
import {
  topOriginsQueryOptions,
  topDestinationsQueryOptions,
} from "@/hooks/useTopLocations";
import {
  rudderAnalytics,
  FlightSearchTrackingEvents,
  type SearchSource,
} from "@/lib/rudderstack";
import type { FlightFormLocation } from "@/types/types";

import { CitiesRecommendations } from "./CitiesRecommendations";
import { LastSearchedAirportList } from "./LastSearchedAirportList";
import { LocationName } from "./LocationName";

type AirportSearchProps = {
  onChange: (value: FlightFormLocation | null) => void;
  analyticsSource: SearchSource;
  field: AirportType;
  excludedLocation?: FlightFormLocation | null;
};

export function AirportSearch({
  onChange,
  field,
  excludedLocation,
  analyticsSource,
}: AirportSearchProps) {
  const intl = useIntl();

  const [input, setInput] = useState("");

  const { previousSearches, addPreviousSearch } = usePreviousAirportSearches();
  const { data: topOrigins } = useQuery(topOriginsQueryOptions());
  const { data: topDestinations } = useQuery(topDestinationsQueryOptions());

  const { data, isSuccess, error } = useAirportSearch(input);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = (value: FlightFormLocation) => {
    onChange(value);
  };

  const checkIsLocationExcluded = (location: FlightFormLocation) => {
    if (!excludedLocation) return false;

    return (
      location.type === excludedLocation.type &&
      location.id === excludedLocation.id
    );
  };

  const filteredPreviousSearches = previousSearches.filter(
    (location) => !checkIsLocationExcluded(location),
  );
  const hasCityRecommendations = field === AirportType.FROM;
  const hasPreviousSearches = filteredPreviousSearches.length > 0;
  const filteredTopLocations = (
    field === AirportType.FROM ? topOrigins : topDestinations
  )?.filter((location) => !checkIsLocationExcluded(location));

  return (
    <div className="flex w-full shrink-0 flex-col">
      <div className="relative flex w-full shrink-0 flex-col gap-4">
        <TextField
          fullWidth
          autoComplete="off"
          autoFocus={useInputAutoFocus({
            inputRef,
          })}
          sx={{
            input: {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
          value={input}
          onChange={(e) => {
            setInput(e.currentTarget.value.slice(0, 30));
          }}
          onFocus={(e) => {
            e.currentTarget.select();
          }}
          slotProps={{
            input: {
              inputRef,
              startAdornment: (
                <InputAdornment position="start">
                  <MagnifyingGlassIcon className="fill-stone-700" />
                </InputAdornment>
              ),
              endAdornment: !!input && (
                <ResetButton
                  aria-label={intl.formatMessage({
                    defaultMessage: "Clear",
                    description:
                      "Generic 'Clear' button label to reset a field or selection",
                  })}
                  onClick={() => {
                    setInput("");
                    inputRef.current?.focus();
                  }}
                />
              ),
            },
          }}
          hiddenLabel
          placeholder={intl.formatMessage({
            defaultMessage: "Search",
            description: "Airport search input placeholder text",
          })}
        />
      </div>

      {input.length > 2 ? (
        error || (isSuccess && data?.length === 0) ? (
          <div className="mt-6 text-center">
            <div className="mb-1 text-base font-medium">
              {intl.formatMessage({
                defaultMessage: "Nothing found",
                description:
                  "Message when the airport search input returns no matching results",
              })}
            </div>
            <div className="text-sm text-text-placeholder">
              {intl.formatMessage({
                defaultMessage: "Please check your input for typos",
                description:
                  "Hint when no airport matches the query, asking user to check for typos",
              })}
            </div>
          </div>
        ) : isSuccess ? (
          <SearchResultList
            data={data}
            getItemKey={(city) => city.id}
            renderItem={(city) => {
              if (
                checkIsLocationExcluded(city) ||
                city.airports.some((airport) =>
                  checkIsLocationExcluded(airport),
                )
              ) {
                return null;
              }

              const selectLocation = (location: FlightFormLocation) => {
                const trackerData = {
                  location_id: location.id,
                  location_name: location.name,
                  location_type: location.type,
                };

                if (field === AirportType.FROM) {
                  rudderAnalytics.track(
                    FlightSearchTrackingEvents.USER_SELECTED_SEARCH_ORIGIN,
                    { ...trackerData, source: analyticsSource },
                  );
                } else {
                  rudderAnalytics.track(
                    FlightSearchTrackingEvents.USER_SELECTED_SEARCH_DESTINATION,
                    { ...trackerData, source: analyticsSource },
                  );
                }

                addPreviousSearch(location);
                handleClick(location);
              };

              return (
                <>
                  <SearchResultButton
                    icon={MapPinIcon}
                    primaryText={<LocationName location={city} />}
                    secondaryText={city.countryName}
                    code={city.code}
                    onClick={() => {
                      selectLocation(city);
                    }}
                  />

                  <ul className="block ps-10">
                    {city.airports.map((airport) => (
                      <li key={airport.id}>
                        <SearchResultButton
                          icon={AirplaneTiltIcon}
                          primaryText={<LocationName location={airport} />}
                          code={airport.code}
                          showIconBackground={false}
                          onClick={() => {
                            selectLocation(airport);
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </>
              );
            }}
          />
        ) : (
          <ul data-testid="loading-indicator" className="mt-4 w-full">
            {[...Array(4)].map((_, index) => (
              <li key={index}>
                <SearchResultButtonSkeleton
                  showIconBackground
                  showLocationCode
                  showSecondaryText
                />

                <ul className="block ps-10">
                  {[...Array(4 - index)].map((_, index) => (
                    <li key={index}>
                      <SearchResultButtonSkeleton showLocationCode />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )
      ) : (
        <>
          {hasCityRecommendations ? (
            <CitiesRecommendations
              onRecommendationClick={(city) => {
                rudderAnalytics.track(
                  FlightSearchTrackingEvents.USER_SELECTED_ORIGIN_RECOMMENDATION,
                  {
                    city_id: city.id,
                    city_name: city.name,
                    source: analyticsSource,
                  },
                );
                handleClick(city);
              }}
              shouldShow={(item) => !checkIsLocationExcluded(item)}
            />
          ) : null}

          {hasPreviousSearches ? (
            <LastSearchedAirportList
              previousSearches={filteredPreviousSearches}
              onClick={(location) => {
                const trackerData = {
                  location_id: location.id,
                  location_name: location.name,
                  location_type: location.type,
                };

                if (field === AirportType.FROM) {
                  rudderAnalytics.track(
                    FlightSearchTrackingEvents.USER_SELECTED_RECENT_ORIGIN,
                    { ...trackerData, source: analyticsSource },
                  );
                } else {
                  rudderAnalytics.track(
                    FlightSearchTrackingEvents.USER_SELECTED_RECENT_DESTINATION,
                    { ...trackerData, source: analyticsSource },
                  );
                }

                handleClick(location);
              }}
            />
          ) : null}

          {filteredTopLocations?.length ? (
            <>
              {hasPreviousSearches || hasCityRecommendations ? (
                <Divider
                  sx={{
                    mt: "calc(var(--spacing) * 4)",
                    borderColor: "var(--color-stroke-neutral-soft)",
                  }}
                />
              ) : null}
              <TopLocationList
                showCode
                getPrimaryText={(location) => (
                  <LocationName location={location} />
                )}
                topLocations={filteredTopLocations}
                onClick={(location) => {
                  const trackerData = {
                    location_id: location.id,
                    location_name: location.name,
                    location_type: location.type,
                  };

                  if (field === AirportType.FROM) {
                    rudderAnalytics.track(
                      FlightSearchTrackingEvents.USER_SELECTED_TOP_ORIGIN,
                      { ...trackerData, source: analyticsSource },
                    );
                  } else {
                    rudderAnalytics.track(
                      FlightSearchTrackingEvents.USER_SELECTED_TOP_DESTINATION,
                      { ...trackerData, source: analyticsSource },
                    );
                  }

                  handleClick(location);
                }}
              />
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
